import { useState } from "react";
import { useNavigate } from "react-router";
import { useLayoutContext } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import { useDryRun } from "@/api/hooks/use-routing";
import { toUserMessage } from "@/lib/errors";
import type { DryRunResponse } from "@/types";

export function RoutingDryRun() {
  const { tenantId } = useLayoutContext();
  const navigate = useNavigate();
  const dryRun = useDryRun();

  const [payload, setPayload] = useState('{\n  "model": "gpt-4o",\n  "messages": []\n}');
  const [feature, setFeature] = useState("");
  const [env, setEnv] = useState("");
  const [priority, setPriority] = useState("");
  const [userId, setUserId] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [result, setResult] = useState<DryRunResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!tenantId) {
    return <EmptyState message="Enter a tenant ID to use dry run" />;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setJsonError(null);
    setSubmitError(null);

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(payload);
    } catch {
      setJsonError("Invalid JSON payload");
      return;
    }

    const body: {
      payload: Record<string, unknown>;
      feature?: string;
      env?: string;
      priority?: string;
      user_id?: string;
    } = {
      payload: parsed,
    };
    if (feature) body.feature = feature;
    if (env) body.env = env;
    if (priority) body.priority = priority;
    if (userId) body.user_id = userId;

    dryRun.mutate(body, {
      onSuccess: (data) => setResult(data),
      onError: (err) => setSubmitError(toUserMessage(err)),
    });
  }

  const inputCls =
    "w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none";
  const labelCls = "block text-xs font-medium text-fg-muted uppercase tracking-wider mb-1";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/routing")}
          className="text-fg-muted hover:text-fg transition-colors text-sm"
        >
          &larr; Back
        </button>
        <h1 className="text-xl font-semibold text-fg">Dry Run</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Tenant pre-filled (read-only) */}
        <div>
          <label className={labelCls}>Tenant ID</label>
          <input
            type="text"
            value={tenantId}
            readOnly
            className={`${inputCls} opacity-60 cursor-not-allowed`}
          />
        </div>

        {/* Payload */}
        <div>
          <label className={labelCls}>Payload (JSON)</label>
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            rows={8}
            className={`${inputCls} font-mono resize-y ${
              jsonError ? "border-block-red" : ""
            }`}
          />
          {jsonError && (
            <p className="mt-1 text-xs text-block-red">{jsonError}</p>
          )}
        </div>

        {/* Optional fields */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div>
            <label className={labelCls}>Feature</label>
            <input
              type="text"
              value={feature}
              onChange={(e) => setFeature(e.target.value)}
              placeholder="e.g. search"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Env</label>
            <input
              type="text"
              value={env}
              onChange={(e) => setEnv(e.target.value)}
              placeholder="e.g. prod"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Priority</label>
            <input
              type="text"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              placeholder="e.g. high"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>User ID</label>
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="e.g. user-123"
              className={inputCls}
            />
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={dryRun.isPending}
          className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white hover:bg-electric-blue/80 disabled:opacity-50 transition-colors"
        >
          {dryRun.isPending ? "Running..." : "Run"}
        </button>
      </form>

      {/* Error feedback */}
      {submitError && (
        <div role="alert" className="rounded-md border border-block-red/30 bg-block-red/10 px-4 py-2 text-sm text-block-red">
          {submitError}
        </div>
      )}

      {/* Result display */}
      {result && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-fg-muted uppercase tracking-wider">
            Result
          </h2>
          <div className="rounded-lg border border-line-subtle bg-surface p-4">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm lg:grid-cols-3">
              <div>
                <dt className="text-xs text-fg-muted uppercase tracking-wider">
                  Chosen Model
                </dt>
                <dd className="mt-0.5 font-mono text-fg">
                  {result.chosen_model}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-fg-muted uppercase tracking-wider">
                  Reason Code
                </dt>
                <dd className="mt-0.5 font-mono text-fg">
                  {result.reason_code ?? "\u2014"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-fg-muted uppercase tracking-wider">
                  Rule ID
                </dt>
                <dd className="mt-0.5 font-mono text-fg">
                  {result.rule_id ?? "\u2014"}
                </dd>
              </div>
            </dl>

            {/* Features */}
            {result.features && Object.keys(result.features).length > 0 && (
              <div className="mt-4 border-t border-line-subtle pt-3">
                <p className="text-xs text-fg-muted uppercase tracking-wider mb-2">
                  Features
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm lg:grid-cols-3">
                  {Object.entries(result.features).map(([key, val]) => (
                    <div key={key} className="flex gap-2">
                      <span className="text-fg-muted">{key}:</span>
                      <span className="font-mono text-fg">
                        {String(val)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
